import { useCallback, useEffect, useMemo, useState } from "react"

import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import DashboardLayout from "@/layouts/DashboardLayout"
import { supabase } from "@/lib/supabase"

// Align with DB enum public.app_role
const ROLES = ["Admin", "Manager", "Driver", "DSP"] as const
type AppRole = (typeof ROLES)[number]

interface ApiKeyRow {
  id: string
  key_hash: string
  role: AppRole
  description: string | null
  expires_at: string | null
  revoked: boolean
  created_at: string | null
}

export default function ApiKeys() {
  const [rows, setRows] = useState<ApiKeyRow[]>([])
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)

  const [newRole, setNewRole] = useState<AppRole>("Admin")
  const [newExpires, setNewExpires] = useState<string>("") // datetime-local value
  const [newDescription, setNewDescription] = useState<string>("")

  const [showKeyDialog, setShowKeyDialog] = useState(false)
  const [plaintextKey, setPlaintextKey] = useState<string>("")
  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  const expiresHelp = useMemo(
    () =>
      newExpires ? new Date(newExpires).toLocaleString() : "Optional; leave empty for non-expiring",
    [newExpires],
  )

  const fetchRows = useCallback(async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from("api_keys")
      .select("id, key_hash, role, description, expires_at, revoked, created_at")
      .order("created_at", { ascending: false })
    if (error) {
      console.error(error)
      toast.error("Failed to load API keys", {
        description: error.message,
      })
    } else {
      setRows((data as ApiKeyRow[]) || [])
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchRows()
  }, [fetchRows])

  const generateRandomKey = (): string => {
    const bytes = new Uint8Array(32)
    crypto.getRandomValues(bytes)
    // base64url encode
    const b64 = btoa(String.fromCharCode(...Array.from(bytes)))
    const base64url = b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
    return `rk_${base64url}` // rk = resourcing key
  }

  const sha256Hex = async (text: string): Promise<string> => {
    const enc = new TextEncoder()
    const digest = await crypto.subtle.digest("SHA-256", enc.encode(text))
    const arr = Array.from(new Uint8Array(digest))
    return arr.map((b) => b.toString(16).padStart(2, "0")).join("")
  }

  const onCreate = async () => {
    try {
      setCreating(true)
      const token = generateRandomKey()
      const hash = await sha256Hex(token)
      const expiresISO = newExpires ? new Date(newExpires).toISOString() : null
      const { data: _data, error } = await supabase
        .from("api_keys")
        .insert({
          key_hash: hash,
          role: newRole,
          expires_at: expiresISO,
          description: newDescription || null,
        })
        .select("id")
        .single()
      if (error) throw error

      setPlaintextKey(token)
      setShowKeyDialog(true)
      setCreateDialogOpen(false)
      setNewExpires("")
      setNewRole("Admin")
      setNewDescription("")
      await fetchRows()
      toast.success("API key created", {
        description: "Copy the key now; it will not be shown again.",
      })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e)
      toast.error("Failed to create API key", { description: msg })
    } finally {
      setCreating(false)
    }
  }

  const onToggleRevoke = async (row: ApiKeyRow) => {
    const newVal = !row.revoked
    const { error } = await supabase.from("api_keys").update({ revoked: newVal }).eq("id", row.id)
    if (error) {
      toast.error("Failed to update", { description: error.message })
    } else {
      toast.success(newVal ? "Key revoked" : "Key unrevoked")
      await fetchRows()
    }
  }

  const onDelete = async (row: ApiKeyRow) => {
    if (!confirm("Delete this API key? This cannot be undone.")) return
    const { error } = await supabase.from("api_keys").delete().eq("id", row.id)
    if (error) {
      toast.error("Failed to delete", { description: error.message })
    } else {
      toast.success("API key deleted")
      await fetchRows()
    }
  }

  return (
    <DashboardLayout>
      <Card>
        <CardHeader>
          <CardTitle>API Keys</CardTitle>
          <CardDescription>
            Admin-only. Create, revoke, and delete API keys. Plaintext keys are only shown once on
            creation.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-muted-foreground">
              {loading ? "Loading…" : `${rows.length} keys`}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={fetchRows} disabled={loading}>
                Refresh
              </Button>
              <Button size="sm" onClick={() => setCreateDialogOpen(true)}>
                New API Key
              </Button>
            </div>
          </div>
          <div className="rounded border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Created</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                    </TableCell>
                    <TableCell>{r.role}</TableCell>
                    <TableCell
                      className="max-w-[280px] truncate"
                      title={r.description || undefined}
                    >
                      {r.description || "—"}
                    </TableCell>
                    <TableCell>
                      {r.expires_at ? new Date(r.expires_at).toLocaleString() : "Never"}
                    </TableCell>
                    <TableCell>
                      {r.revoked ? (
                        <span className="text-red-600">Revoked</span>
                      ) : (
                        <span className="text-green-600">Active</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button variant="outline" size="sm" onClick={() => onToggleRevoke(r)}>
                        {r.revoked ? "Unrevoke" : "Revoke"}
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => onDelete(r)}>
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
                      No API keys yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create API Key Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create API Key</DialogTitle>
            <DialogDescription>
              Configure the key properties, then click Create. The plaintext key will be shown once
              after creation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Role</Label>
              <Select value={newRole} onValueChange={(v) => setNewRole(v as AppRole)}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Input
                className="mt-1"
                type="text"
                placeholder="What is this key for?"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
              />
              <div className="text-xs text-muted-foreground mt-1">
                Optional; helps identify usage.
              </div>
            </div>
            <div>
              <Label>Expires at</Label>
              <Input
                className="mt-1"
                type="datetime-local"
                value={newExpires}
                onChange={(e) => setNewExpires(e.target.value)}
              />
              <div className="text-xs text-muted-foreground mt-1">{expiresHelp}</div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="button" onClick={onCreate} disabled={creating}>
                {creating ? "Creating…" : "Create"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showKeyDialog} onOpenChange={setShowKeyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Copy your API key</DialogTitle>
            <DialogDescription>
              This is the only time the plaintext key will be shown. Store it securely.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            <Label>API Key</Label>
            <Input readOnly value={plaintextKey} className="mt-1 font-mono" />
            <div className="mt-2 flex gap-2">
              <Button
                type="button"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(plaintextKey)
                    toast.success("Copied to clipboard")
                  } catch (err) {
                    console.error("Failed to copy to clipboard", err)
                  }
                }}
              >
                Copy
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowKeyDialog(false)}>
                Close
              </Button>
            </div>
          </div>
          <DialogFooter />
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  )
}
